import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  TextField,
  Grid,
  Paper,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';

const RejectedShops = () => {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedShop, setSelectedShop] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editDocuments, setEditDocuments] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [userId, setUserId] = useState(null);
  const { can } = usePermissions();
  const canEditRetry = can('merchants.rejected.editRetry');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [searchText, setSearchText] = useState('');

  const navigate = useNavigate();

  const editableFields = [
    { key: 'sellerName', label: 'Seller Name', required: true },
    { key: 'mobileNumber', label: 'Mobile Number', required: true },
    { key: 'businessName', label: 'Business Name', required: true },
    { key: 'addressLine1', label: 'Address Line 1', required: true },
    { key: 'addressLine2', label: 'Address Line 2', required: true },
    { key: 'pincode', label: 'Pincode', required: true },
    { key: 'emailId', label: 'Email', required: true },
    { key: 'pan', label: 'PAN', required: true },
    { key: 'promoterAadhaar', label: 'Aadhaar Number', required: true },
    { key: 'dob', label: 'Date of Birth', required: true },
    { key: 'gender', label: 'Gender', required: true, type: 'select', options: ['MALE', 'FEMALE', 'OTHER'] },
    { key: 'mcc', label: 'MCC Code', required: true },
    { key: 'shopType', label: 'Shop Type', required: true },
    { key: 'bussiness', label: 'Business Type', required: true },
    { key: 'settlementAccountNumber', label: 'Bank Account Number', required: true },
    { key: 'settlementAccountIfsc', label: 'IFSC Code', required: true },
    { key: 'gstNumber', label: 'GST Number' },
    { key: 'llpInCin', label: 'LLP/CIN Number' },
    { key: 'latitude', label: 'Latitude' },
    { key: 'longitude', label: 'Longitude' },
    { key: 'city', label: 'City' },
    { key: 'district', label: 'District' },
    { key: 'stateCode', label: 'State' },
    { key: 'ownershipType', label: 'Ownership Type', type: 'select', options: ['PROPRIETARY', 'PARTNERSHIP', 'PRIVATE_LIMITED', 'PUBLIC_LIMITED', 'LLP'] },
  ];

  const documentFields = [
    { key: 'bankProof', label: 'Bank Proof' },
    { key: 'aadhaarDocument', label: 'Aadhaar Front' },
    { key: 'aadhaarBackDocument', label: 'Aadhaar Back' },
    { key: 'panDocument', label: 'PAN Document' },
    { key: 'shopPhoto', label: 'Shop Photo' },
    { key: 'gstCertificate', label: 'GST Certificate' },
  ];

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUserId(decodedToken.userId);
      } catch (error) {
        setSnackbarMessage('Error decoding user token');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        navigate('/login');
      }
    } else {
      setSnackbarMessage('No user token found');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    if (userId) {
      fetchShops();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, page, pageSize]);

  const fetchShops = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: pageSize,
        externalSource: 'acemoney',
        acemoneyRegistrationStatus: 'failed',
      };

      const response = await axios.get(`${API_BASE_URL}/shops/${userId}`, { params });
      setShops(response.data.shops || []);
      setTotalRows(response.data.totalShops || 0);
    } catch (error) {
      const status = error && error.response ? error.response.status : null;
      const msg = (error && error.response && error.response.data && error.response.data.message) ? error.response.data.message : 'Error fetching shops';
      if (status === 500) {
        setSnackbarMessage('Server error. Please try again later.');
      } else if (status === 404) {
        setSnackbarMessage('API endpoint not found');
      } else {
        setSnackbarMessage(msg);
      }
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setShops([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchClear = () => {
    setSearchText('');
  };

  const filteredShops = shops.filter((shop) => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return (
      (shop.businessName && shop.businessName.toLowerCase().includes(searchLower)) ||
      (shop.sellerName && shop.sellerName.toLowerCase().includes(searchLower)) ||
      (shop.pincode && shop.pincode.includes(searchText)) ||
      (shop.mobileNumber && shop.mobileNumber.includes(searchText)) ||
      (shop.pan && shop.pan.toLowerCase().includes(searchLower))
    );
  });

  // Detail Dialog
  const handleDetailDialogOpen = (shop) => {
    setSelectedShop(shop);
    setDetailDialogOpen(true);
  };

  const handleDetailDialogClose = () => {
    setDetailDialogOpen(false);
    setSelectedShop(null);
  };

  // Edit & Retry Dialog
  const handleEditDialogOpen = (shop) => {
    if (!canEditRetry) return;
    setSelectedShop(shop);
    const formData = {};
    editableFields.forEach(({ key }) => {
      formData[key] = shop[key] || '';
    });
    setEditFormData(formData);
    setEditDocuments({});
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedShop(null);
    setEditFormData({});
    setEditDocuments({});
  };

  const handleFieldChange = (key, value) => {
    setEditFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleDocumentUpload = (key, file) => {
    if (!file || !canEditRetry) return;
    if (file.size > 2 * 1024 * 1024) {
      setSnackbarMessage('File size must be less than 2MB');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setEditDocuments((prev) => ({ ...prev, [key]: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
  };

  const handleSubmitRetry = async () => {
    if (!canEditRetry) return;
    setSubmitting(true);
    try {
      const payload = { ...editFormData };

      // Include documents if any were re-uploaded
      if (Object.keys(editDocuments).length > 0) {
        payload.documents = editDocuments;
      }

      const response = await axios.put(
        `${API_BASE_URL}/acemoney-registration/retry/${userId}/${selectedShop._id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('token')}`,
          },
        }
      );

      if (response.data.success) {
        setSnackbarMessage('Registration retry submitted successfully');
        setSnackbarSeverity('success');
      } else {
        setSnackbarMessage(response.data.message || 'Retry submitted but AceMoney registration failed again');
        setSnackbarSeverity('warning');
      }
      setSnackbarOpen(true);
      handleConfirmDialogClose();
      handleEditDialogClose();
      setTimeout(() => {
        fetchShops();
      }, 1500);
    } catch (error) {
      const errorMsg = (error && error.response && error.response.data && error.response.data.message) ? error.response.data.message : (error.message || 'Error submitting retry');
      setSnackbarMessage(errorMsg);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      handleConfirmDialogClose();
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      field: 'merchantId',
      headerName: 'Merchant ID',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.dark' }}>
          {params.row.acemoneyUsercode || params.row.sellerIdentifier || params.row.vpaCode || params.row.merchantId || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'sellerName',
      headerName: 'Merchant Name',
      width: 170,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'businessName',
      headerName: 'Shop Name',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'shopType',
      headerName: 'Shop Type',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: '#475569' }}>
          {params.value || params.row.bussiness || 'General'}
        </Typography>
      ),
    },
    {
      field: 'stateCode',
      headerName: 'STATE',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value || params.row.state || 'Kerala'}
        </Typography>
      ),
    },
    {
      field: 'district',
      headerName: 'DISTRICT',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'village',
      headerName: 'CONSTITUENCY',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: '#475569' }}>
          {params.row.city || params.row.post || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'pincode',
      headerName: 'Pincode',
      width: 120,
      renderCell: (params) => (
        <Chip label={params.value || 'N/A'} size="small" variant="outlined" />
      ),
    },
    {
      field: 'assignedQPayCenter',
      headerName: 'Q-Pay Center ID',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'assignedStateHead',
      headerName: 'State Head ID',
      width: 170,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'acemoneyFailureReason',
      headerName: 'Rejected Reason',
      width: 250,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'error.main', whiteSpace: 'normal', lineHeight: 1.3, fontWeight: 500 }}>
          {params.value || params.row.failureReason || 'Rejected by Bank'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: canEditRetry ? 220 : 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => handleDetailDialogOpen(params.row)}
          >
            Details
          </Button>
          {canEditRetry && (
            <Button
              variant="contained"
              color="warning"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => handleEditDialogOpen(params.row)}
            >
              Edit & Retry
            </Button>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br /><br />

      <Box sx={{ width: '100%', p: 3 }}>
        <Typography variant="h4" gutterBottom color="primary">
          Rejected Shops
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          View and retry failed AceMoney registrations
        </Typography>

        {/* Search */}
        <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by name, PAN, pincode, or contact"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleSearchClear}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        {/* Data Grid */}
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={filteredShops}
            columns={columns}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 25, 50]}
            pagination
            page={page}
            rowCount={totalRows}
            paginationMode="server"
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            getRowId={(row) => row._id}
            disableSelectionOnClick
            loading={loading}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', p: 3 }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No rejected shops found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    All AceMoney registrations are successful or pending
                  </Typography>
                </Box>
              ),
              loadingOverlay: () => (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              ),
            }}
          />
        </Box>

        {/* Detail Dialog */}
        <Dialog open={detailDialogOpen} onClose={handleDetailDialogClose} fullWidth maxWidth="md">
          <DialogTitle>
            <Typography variant="h5" color="primary">
              Shop Details
            </Typography>
          </DialogTitle>
          <DialogContent>
            {selectedShop && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Paper elevation={3} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom color="primary">Business Information</Typography>
                    <Typography><strong>Business Name:</strong> {selectedShop.businessName}</Typography>
                    <Typography><strong>Display Name:</strong> {selectedShop.displayname || 'N/A'}</Typography>
                    <Typography><strong>Business Type:</strong> {selectedShop.bussiness}</Typography>
                    <Typography><strong>Shop Type:</strong> {selectedShop.shopType}</Typography>
                    <Typography><strong>MCC:</strong> {selectedShop.mcc}</Typography>
                    <Typography><strong>Ownership Type:</strong> {selectedShop.ownershipType || 'N/A'}</Typography>
                    <Typography><strong>GST Number:</strong> {selectedShop.gstNumber || 'N/A'}</Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper elevation={3} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom color="primary">Agent Details</Typography>
                    <Typography><strong>Name:</strong> {selectedShop.sellerName}</Typography>
                    <Typography><strong>Contact:</strong> {selectedShop.mobileNumber}</Typography>
                    <Typography><strong>Email:</strong> {selectedShop.emailId || 'N/A'}</Typography>
                    <Typography><strong>Gender:</strong> {selectedShop.gender || 'N/A'}</Typography>
                    <Typography><strong>DOB:</strong> {selectedShop.dob || 'N/A'}</Typography>
                    <Typography><strong>PAN:</strong> {selectedShop.pan}</Typography>
                    <Typography><strong>Aadhaar:</strong> {selectedShop.promoterAadhaar || 'N/A'}</Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper elevation={3} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom color="primary">Location</Typography>
                    <Typography><strong>Address:</strong> {selectedShop.addressLine1}</Typography>
                    {selectedShop.addressLine2 && <Typography>{selectedShop.addressLine2}</Typography>}
                    <Typography><strong>City:</strong> {selectedShop.city || 'N/A'}</Typography>
                    <Typography><strong>District:</strong> {selectedShop.district || 'N/A'}</Typography>
                    <Typography><strong>State:</strong> {selectedShop.stateCode || 'N/A'}</Typography>
                    <Typography><strong>Pincode:</strong> {selectedShop.pincode}</Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper elevation={3} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom color="primary">Bank Details</Typography>
                    <Typography><strong>Account Number:</strong> {selectedShop.settlementAccountNumber || 'N/A'}</Typography>
                    <Typography><strong>IFSC Code:</strong> {selectedShop.settlementAccountIfsc || 'N/A'}</Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper elevation={3} sx={{ p: 2, bgcolor: 'error.50' }}>
                    <Typography variant="h6" gutterBottom color="error">Failure Information</Typography>
                    <Typography><strong>Failure Reason:</strong> {selectedShop.acemoneyFailureReason || 'Unknown'}</Typography>
                    {selectedShop.acemoneyResponse && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>Raw AceMoney Response:</Typography>
                        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.100', maxHeight: 200, overflow: 'auto' }}>
                          <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {typeof selectedShop.acemoneyResponse === 'string'
                              ? selectedShop.acemoneyResponse
                              : JSON.stringify(selectedShop.acemoneyResponse, null, 2)}
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDetailDialogClose} color="secondary">
              Close
            </Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={<EditIcon />}
              onClick={() => {
                handleDetailDialogClose();
                handleEditDialogOpen(selectedShop);
              }}
            >
              Edit & Retry
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit & Retry Dialog */}
        <Dialog open={editDialogOpen} onClose={handleEditDialogClose} fullWidth maxWidth="md">
          <DialogTitle>
            <Typography variant="h5" color="primary">
              Edit & Retry Registration
            </Typography>
            {selectedShop && (
              <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                Failure Reason: {selectedShop.acemoneyFailureReason || 'Unknown'}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {editableFields.map(({ key, label, required, type, options }) => (
                <Grid item xs={12} sm={6} key={key}>
                  {type === 'select' ? (
                    <FormControl fullWidth>
                      <InputLabel>{label}</InputLabel>
                      <Select
                        value={editFormData[key] || ''}
                        label={label}
                        onChange={(e) => handleFieldChange(key, e.target.value)}
                      >
                        {options.map((opt) => (
                          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      fullWidth
                      label={label}
                      value={editFormData[key] || ''}
                      onChange={(e) => handleFieldChange(key, e.target.value)}
                      required={required}
                    />
                  )}
                </Grid>
              ))}

              {/* Document Uploads */}
              <Grid item xs={12}>
                <Typography variant="h6" color="primary" sx={{ mt: 2, mb: 1 }}>
                  Re-upload Documents (optional)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Only upload documents that need to be changed. Max 2MB per file.
                </Typography>
              </Grid>
              {documentFields.map(({ key, label }) => (
                <Grid item xs={12} sm={6} key={key}>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    color={editDocuments[key] ? 'success' : 'primary'}
                  >
                    {editDocuments[key] ? `${label} (uploaded)` : `Upload ${label}`}
                    <input
                      type="file"
                      hidden
                      accept="image/*,.pdf"
                      onChange={(e) => handleDocumentUpload(key, e.target.files[0])}
                    />
                  </Button>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEditDialogClose} color="secondary">
              Cancel
            </Button>
            <Button
              onClick={handleOpenConfirmDialog}
              color="primary"
              variant="contained"
            >
              Submit Retry
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog open={confirmDialogOpen} onClose={handleConfirmDialogClose}>
          <DialogTitle>
            <Typography variant="h6" color="warning.main">
              Confirm Retry Registration
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to resubmit this shop registration to AceMoney?
            </Typography>
            <Paper elevation={2} sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
              <Typography><strong>Shop:</strong> {editFormData.businessName}</Typography>
              <Typography><strong>Seller:</strong> {editFormData.sellerName}</Typography>
              <Typography><strong>PAN:</strong> {editFormData.pan}</Typography>
              <Typography><strong>Contact:</strong> {editFormData.mobileNumber}</Typography>
              {Object.keys(editDocuments).length > 0 && (
                <Typography><strong>Documents Updated:</strong> {Object.keys(editDocuments).join(', ')}</Typography>
              )}
            </Paper>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              The shop data will be updated and re-sent to AceMoney for registration.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleConfirmDialogClose} color="secondary" disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitRetry}
              color="primary"
              variant="contained"
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={24} /> : 'Confirm Retry'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default RejectedShops;
